import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Phase phase-fix-admin-forms-tab-crash (§4.3) — the app's first error
 * boundary.
 *
 * The Admin Forms tab crashed with an unguarded `.length` read and took the
 * WHOLE submission page down to a raw React Router error screen. The
 * underlying data bug is fixed, but a render error in one tab should never be
 * able to blank the page again: wrap each tab panel so a failure degrades to
 * an inline error card next to the still-working tab bar.
 *
 * Deliberately small — there is no other error-boundary pattern in the repo to
 * follow (searched). React only supports class components for error
 * boundaries, so this is the one class component in web/src.
 */
export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /** Shown above the error text, e.g. "Admin Forms". */
  readonly label?: string;
  /**
   * Changing this value resets the boundary — pass the active tab key so
   * switching tabs clears a previous tab's error instead of latching it.
   */
  readonly resetKey?: string | number;
  /**
   * phase-fix-validation-banner-crash — when true, the fallback becomes a
   * full-route error card with a Reload button and a "report this" hint
   * instead of the compact inline card. Use for route-level boundaries where
   * a crash would otherwise white-screen the whole page (there is no tab bar
   * left to navigate away with). Defaults to false → the original inline card.
   */
  readonly showReload?: boolean;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
  readonly resetKey: string | number | undefined;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  /** Clear the error when the caller swaps resetKey (e.g. a different tab). */
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const { label, showReload } = this.props;
    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        style={{
          margin: 16,
          padding: 16,
          border: '1px solid var(--red, #dc2626)',
          background: 'var(--red-bg, #fef2f2)',
          borderRadius: 8,
          color: 'var(--red, #dc2626)',
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          {label ? `${label} couldn’t be displayed` : 'This section couldn’t be displayed'}
        </div>
        <div style={{ color: 'var(--text-muted, #6b7280)' }}>
          {showReload
            ? 'Something went wrong rendering this page. Reloading usually clears it. If it keeps happening, please report it so we can fix the cause.'
            : 'Something went wrong rendering this section. The rest of the page still works — try another tab, or reload to try again.'}
        </div>
        <div
          data-testid="error-boundary-message"
          style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted, #6b7280)' }}
        >
          {error.message}
        </div>
        {showReload && (
          <button
            type="button"
            data-testid="error-boundary-reload"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 12,
              background: 'var(--red, #dc2626)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        )}
      </div>
    );
  }
}
